import { OidcClient } from "@axa-fr/react-oidc";
import { useQuery } from "@tanstack/react-query";
import { type ComponentType, createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from "react";
import { AdmissionErrorCode, type CurrentAgentSessionDto, useSessionApi } from "@/api/session";
import logoUrl from "@/assets/brand/logo-128.png";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/locales/i18n-provider";
import { isSessionExpiredError } from "@/request/request-client/session-expired";
import { type IdentityScope, useIdentityScope } from "./access-token-provider";
import { CURRENT_SESSION_QUERY_KEY, clearCurrentSessionCache, readCurrentSessionCache, writeCurrentSessionCache } from "./session-cache";
import { useSessionExpiration } from "./use-session-expiration";

type SessionContextValue = {
  agent: CurrentAgentSessionDto;
  /** 当前有效权限集，用于菜单与按钮显隐。 */
  permissions: ReadonlySet<string>;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
};

const SessionContext = createContext<SessionContextValue | null>(null);

/** 读取当前会话。必须在 `SessionProvider` 之内使用。 */
export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession 必须在 SessionProvider 之内使用。");
  }
  return value;
}

/** 只取判权函数的便捷写法。 */
export function usePermission() {
  const { can, canAny } = useSession();
  return { can, canAny };
}

function readErrorCode(error: unknown): string | null {
  const requestError = error as { data?: { error?: { code?: unknown } }; response?: { data?: { error?: { code?: unknown } } } };
  const code = requestError?.data?.error?.code ?? requestError?.response?.data?.error?.code;
  return typeof code === "string" ? code : null;
}

function readStatus(error: unknown): number | null {
  const requestError = error as { status?: unknown; response?: { status?: unknown } };
  const status = requestError?.status ?? requestError?.response?.status;
  return typeof status === "number" ? status : null;
}

export type AdmissionNoticeProps = { title: string; detail: string };

function AdmissionNotice({ title, detail }: AdmissionNoticeProps) {
  const { t } = useI18n();

  const handleRelogin = () => {
    const client = OidcClient.get();
    if (!client) {
      throw new Error("OIDC client is unavailable.");
    }
    const { pathname, search, hash } = window.location;
    void client.loginAsync(`${pathname}${search}${hash}`, { prompt: "login" });
  };

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-6 py-12">
      <section className="w-full max-w-130 rounded-xl border bg-card p-8 shadow-sm">
        <img alt="Tickbird" className="mb-6 size-14 rounded-lg" src={logoUrl} />
        <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">{detail}</p>
        <div className="mt-6">
          <Button onClick={() => window.location.reload()}>{t("auth.reload")}</Button>
          <Button variant="link" onClick={handleRelogin}>
            {t("auth.relogin")}
          </Button>
        </div>
      </section>
    </main>
  );
}

type SessionProviderProps = {
  children: ReactNode;
  /**
   * 是否把会话写进 `sessionStorage`。嵌入端固定为 false：缓存里虽然没有 Token，
   * 但嵌入端的身份边界由宿主会话决定，浏览器侧无法可靠感知宿主换人，
   * 宁可每次握手后重查一次 `session/current`（docs/embedded-workbench-design.md §9.5）。
   */
  persistCache?: boolean;
  /** 准入被拒时的通知回调，嵌入端据此向宿主发 `tickbird:forbidden`。 */
  onAdmissionDenied?: (code: string | null) => void;
  /** 准入提示的呈现组件，嵌入端换成紧凑版式。 */
  noticeComponent?: ComponentType<AdmissionNoticeProps>;
  /** 加载态版式。嵌入端用 inline：占满 iframe 容器且不带语言入口。 */
  loadingVariant?: "page" | "inline";
};

/**
 * 会话与权限边界。认证之后向 Api.Admin 取当前 Agent 与有效权限集；
 * 准入被拒（未开通 / 已停用）时渲染引导页而不是把用户放进工作台。
 *
 * 人员由管理员从 IAM 目录开通，登录本身不再自动建档
 * （docs/identity-and-authz.md §5）。
 *
 * 身份作用域来自 `AccessTokenProvider.getIdentityScope()` 而不是 ID Token：
 * 嵌入端没有 ID Token，也不在 `OidcProvider` 之内（嵌入设计 §9.1、AR-29）。
 */
export function SessionProvider({ children, persistCache = true, onAdmissionDenied, noticeComponent, loadingVariant = "page" }: SessionProviderProps) {
  const { t } = useI18n();
  const sessionApi = useSessionApi();
  const Notice = noticeComponent ?? AdmissionNotice;
  const expired = useSessionExpiration() !== "active";
  const latestScope = useIdentityScope();
  const previousScope = useRef<IdentityScope>(latestScope);
  const cacheScope = expired ? previousScope.current : latestScope;
  useEffect(() => {
    if (!expired) previousScope.current = latestScope;
  }, [expired, latestScope]);
  const cachedSession = useMemo(() => (persistCache && cacheScope ? readCurrentSessionCache(cacheScope) : undefined), [cacheScope, persistCache]);

  const {
    data: agent,
    dataUpdatedAt,
    error,
    isPending,
  } = useQuery({
    // 身份加入 key，避免退出后同一 SPA 内换人登录时复用上一位 Agent。
    queryKey: [...CURRENT_SESSION_QUERY_KEY, cacheScope?.key ?? "uncached"],
    queryFn: () => sessionApi.getCurrent(),
    initialData: cachedSession?.data,
    initialDataUpdatedAt: cachedSession?.cachedAt,
    enabled: !expired,
    // 会话跟随 OIDC 令牌与显式 invalidation 失效，不按组件挂载次数重复获取。
    staleTime: Infinity,
    // 准入被拒是确定性结果，重试没有意义。
    retry: (failureCount, err) => !isSessionExpiredError(err) && readStatus(err) !== 403 && failureCount < 1,
  });

  useEffect(() => {
    if (!expired && persistCache && cacheScope && agent) {
      writeCurrentSessionCache(cacheScope, agent, dataUpdatedAt);
    }
  }, [agent, cacheScope, dataUpdatedAt, expired, persistCache]);

  useEffect(() => {
    if (error) {
      clearCurrentSessionCache();
    }
  }, [error]);

  useEffect(() => {
    if (error) {
      onAdmissionDenied?.(readErrorCode(error));
    }
  }, [error, onAdmissionDenied]);

  const value = useMemo<SessionContextValue | null>(() => {
    if (!agent) {
      return null;
    }

    const permissions = new Set(agent.permissions);
    const can = (permission: string) => permissions.has(permission);

    return {
      agent,
      permissions,
      can,
      canAny: (...candidates: string[]) => candidates.some(can),
    };
  }, [agent]);

  // 后台会话失效时保留已挂载的工作台（包括编辑中的表单）。
  if (value && expired) {
    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
  }

  if (expired) {
    return <main className="grid min-h-dvh place-items-center bg-background p-6 text-muted-foreground">{t("auth.sessionExpired.detail")}</main>;
  }

  if (isPending) {
    return <LoadingScreen detail={t("auth.session.loadingDetail")} title={t("auth.session.loadingTitle")} variant={loadingVariant} />;
  }

  if (error || !value) {
    switch (readErrorCode(error)) {
      case AdmissionErrorCode.NotProvisioned:
        return <Notice title={t("auth.admission.notProvisioned.title")} detail={t("auth.admission.notProvisioned.detail")} />;
      case AdmissionErrorCode.NoRoles:
        return <Notice title={t("auth.admission.noRoles.title")} detail={t("auth.admission.noRoles.detail")} />;
      case AdmissionErrorCode.Inactive:
        return <Notice title={t("auth.admission.inactive.title")} detail={t("auth.admission.inactive.detail")} />;
      case AdmissionErrorCode.SubjectMissing:
        return <Notice title={t("auth.admission.subjectMissing.title")} detail={t("auth.admission.subjectMissing.detail")} />;
      default:
        return <Notice title={t("auth.admission.failed.title")} detail={t("auth.admission.failed.detail")} />;
    }
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
